import { generateContextForPreviewUrl } from "@app/cuePreview";
import RenderArticleBlocks from "@blocks/RenderArticleBlocks/RenderArticleBlocks";
import { LOCALHOST, URL_ORIGIN } from "@config/MetaTagsConstants";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { formatDate } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import WordPuzzle from "@modules/WordPuzzle/WordPuzzle";
import { Route } from "@route-types/Article/+types/Article";
import ArticleContentCard from "@routes/Article/ArticleContentCard";
import ArticleInfiniteScroll from "@routes/Article/ArticleInfiniteScroll";
import {
  getAuthorsName,
  getFirstParagraph,
} from "@routes/Article/ArticleMetaData";
import ErrorPage from "@routes/ErrorPage";
import { fetchContext, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import {
  createMetaTags,
  MetaTagsProps,
  USFArticleSchema,
} from "@sphtech/web2-seo-schema";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { PartialCueArticleData } from "@typings/Opensearch";
import { ArticlePayloadResponseType } from "@typings/PagePayloadType";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchArticleData } from "./Article.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    ArticlePayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
  return [{ title: "Page Not found" }];
}

export default function ArticlePage() {
  const dataLoaderResponse: TRouteWithRedirect<
    ArticlePayloadResponseType,
    string
  > = useLoaderData();
  const adsSettings: AdsSettings = useGlobalAdSetting();
  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const { gsChannels } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const { isScreenLG } = useWindowSize();

  if (dataLoaderResponse.type !== ResponseType.SUCCESS) {
    return <ErrorPage message="Something went wrong" />;
  }
  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const mainArticle = dataLoaderResponse.payload.article;
  const sectionName = mainArticle.sectionName;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri || "";
  // Check Cue preview
  const isCuePreview = dataLoaderResponse.payload.isPreview
    ? dataLoaderResponse.payload.isPreview
    : false;

  const articlePageTarget: ArticleAdPageTargetingType[] = [
    { key: "page_number", value: "1" },
    { key: "tmarticleid", value: mainArticle.id },
    {
      key: "tmtags",
      value: mainArticle.tags?.map((tags) => tags.name).join(", ") || "",
    },
  ];

  const handleNewArticle = (
    newArticle: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => {
    if (viewPortChange) {
      setTimeout(() => {
        TmCustomGaEvent({
          event: "virtualPV",
          articleid: newArticle.id,
          byline: newArticle.authors
            ? newArticle.authors.map((author) => author.name).join(", ")
            : "",
          chapter1: "",
          contenttype: "article",
          cue_articleid: newArticle.id,
          keyword: newArticle.tags
            ? newArticle.tags.map((tags) => tags.name).join(", ")
            : "",
          landingPage: origin + mainArticle.urlPath,
          level2: newArticle.sectionName,
          nextNodeTitle: newArticle.title,
          nextNodeUrl: origin + newArticle.urlPath,
          pubdate: formatDate(newArticle.dateCreated, "yyyy-MM-dd HH:mm:ss"),
          title: mainArticle.title,
          pagination: pageNumber,
        });
      }, 300);
    }
  };

  return (
    <>
      <USFArticleSchema
        url={`${URL_ORIGIN}${mainArticle.urlPath}`}
        title={mainArticle.title}
        images={[
          {
            url: mainArticle.imageField?.url || "",
            width: 1140,
            height: 760,
          },
        ]}
        datePublished={formatDate(
          mainArticle.dateCreated,
          "yyyy-MM-dd HH:mm:ss",
        )}
        dateModified={formatDate(mainArticle.dateEdited, "yyyy-MM-dd HH:mm:ss")}
        authorName={
          mainArticle.authors ? getAuthorsName(mainArticle.authors) : ""
        }
        authorUrl={
          mainArticle.authors && mainArticle.authors.length > 0
            ? `${URL_ORIGIN}${mainArticle.authors[0].urlPath}`
            : ""
        }
        publisherName="TamilMurasu"
        publisherLogo={{
          url: "https://www.tamilmurasu.com.sg/web2-assets/tm_logo.webp",
          width: 630,
          height: 144,
        }}
        description={getFirstParagraph(mainArticle.articleBody)}
        isPaywalled={false}
        speakable={{
          cssSelector: [".article-headline", ".article-summary"],
        }}
        {...(mainArticle.authors &&
          mainArticle.authors.length > 1 && {
            authorSameAs: mainArticle.authors.map(
              (author) => `${URL_ORIGIN}${author.urlPath}`,
            ),
          })}
      />
      <MainContainer
        sectionName={sectionName}
        pageAdTargetValue={pageAdTargetValue}
        suppliedAdsSettings={adsSettings}
        gsChannels={gsChannels}
        isArticlePage={true}
      >
        <div id={mainArticle.id} className="article-card">
          <WrapperBlock className="hover:text flex flex-col lg:flex-row justify-between">
            <ArticleContentCard
              articleData={mainArticle}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              hasTaboola={adsSettings.isTaboolaEnabled}
              gsChannels={gsChannels}
              isCuePreview={isCuePreview}
            />
            {isScreenLG ? (
              <SideBlock
                sectionName={sectionName}
                pageAdTargetValue={pageAdTargetValue}
                articlePageTarget={articlePageTarget}
                gsChannels={gsChannels}
                promoteEpaper={false}
                enableEditorialSection={false}
                epaperCoverImageUrl={epaperCoverImageUrl}
              />
            ) : (
              <>
                {/* Mobile infinite scroll view */}
                {/* Render first block on default article */}
                <RenderArticleBlocks
                  index={0}
                  pageNumber={0}
                  pageUrl={mainArticle.urlPath ? mainArticle.urlPath : ""}
                  hasTaboola={adsSettings.isTaboolaEnabled}
                  articleData={mainArticle}
                  pageAdTargetValue={pageAdTargetValue}
                  gsChannels={gsChannels}
                />
                <div className="border-y border-solid border-pink-300 mt-5">
                  <AdsImu2
                    sectionName={sectionName}
                    pageAdTargetType={pageAdTargetValue}
                    rootClassName="mt-5"
                    articlePageTarget={articlePageTarget}
                    gsChannels={gsChannels}
                  />
                </div>
              </>
            )}
          </WrapperBlock>
        </div>

        <ArticleInfiniteScroll
          mainArticle={mainArticle}
          pageAdTargetValue={pageAdTargetValue}
          onNewArticle={handleNewArticle}
          hasTaboola={adsSettings.isTaboolaEnabled}
          gsChannels={gsChannels}
          isCuePreview={isCuePreview}
        />
        <WordPuzzle isCatfishEnabled={adsSettings.isCatfishEnabled} />
      </MainContainer>
    </>
  );
}

export async function loader({ request, params, context }: Route.LoaderArgs) {
  // Check if previewData exists (optional field)
  if (context.previewData) {
    const result = generateContextForPreviewUrl(
      context.previewData as PartialCueArticleData,
    );
    return result;
  }

  const url = new URL(request.url, LOCALHOST);
  const pathname = url.pathname;

  let data: Awaited<ReturnType<typeof fetchArticleData>> | null = null;

  if (params.id && pathname.includes("article")) {
    // Article ID-based route: /article/:id
    data = await fetchArticleData({ articleId: params.id });
  } else {
    // URL-based routes: all other variants
    data = await fetchArticleData({ urlPath: pathname });
  }

  const dataPayload =
    typeof data === "object" && "payload" in data ? data.payload : [];
  const article =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;

  if (!article) return { ...data };

  const gaData =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "gaData" in dataPayload &&
    dataPayload.gaData;

  const gtmData = getGTMData(gaData as GADataProps);

  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<ArticlePayloadResponseType, string>
  >;
}
